import { EyeOff } from "lucide-react";
import {
  DEFAULT_MAINTENANCE_MESSAGE,
  DEFAULT_MAINTENANCE_TITLE,
  DEFAULT_PROJECT_MAINTENANCE_MESSAGE,
  maintenanceStatus,
  type MaintenanceSettings,
} from "@shared/maintenance";

/**
 * A „Hamarosan" mód szerkesztő mezői — KÖZÖS az oldal-szintű (Oldal
 * beállítások → Oldal állapota) és a projekt-szintű (projekt szerkesztő)
 * kapcsolóhoz, hogy a két helyen ne csússzon szét a viselkedés és a szöveg.
 */
export function MaintenanceFields({
  value,
  onChange,
  scope,
}: {
  value: MaintenanceSettings | null | undefined;
  onChange: (patch: Partial<MaintenanceSettings>) => void;
  /** "site" → a teljes oldal, "project" → egyetlen projekt tartalma. */
  scope: "site" | "project";
}) {
  const v = value ?? {};
  const isSite = scope === "site";
  const status = maintenanceStatus(v);
  return (
    <div className="space-y-4">
      <p
        className={`text-xs px-3 py-2 rounded border flex items-start gap-2 ${
          status.active
            ? "bg-amber-50 border-amber-200 text-amber-800"
            : "bg-green-50 border-green-200 text-green-800"
        }`}
      >
        {status.active && <EyeOff size={14} className="mt-0.5 shrink-0" />}
        <span>
          {isSite
            ? status.text
            : status.active
              ? status.text.replace(
                  "a látogatók nem látják a tartalmat",
                  "a látogatók nem látják a projekt tartalmát",
                )
              : status.text.replace(
                  "Az oldal ÉLES — a látogatók a tartalmat látják.",
                  "A projekt ÉLES — a látogatók a tartalmát látják.",
                )}
        </span>
      </p>

      <label className="flex items-start gap-2 text-sm">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={v.enabled ?? false}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        <span>
          <span className="font-semibold">„Hamarosan" mód bekapcsolása</span>
          <span className="block text-xs text-brand-500">
            {isSite
              ? "A látogatók a tartalom helyett a lenti szöveget látják, és a keresők sem indexelik az oldalt."
              : "A projekt a listában marad (név + borítókép + „Hamarosan” címke), de a leírása, galériája, alaprajzai, lakásai és árai nem látszanak, és a keresők sem indexelik."}
          </span>
        </span>
      </label>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="label">Időzítés — ekkortól (opcionális)</label>
          <input
            type="datetime-local"
            className="input"
            value={isoToLocalInput(v.startAt)}
            onChange={(e) =>
              onChange({ startAt: localInputToIso(e.target.value) })
            }
          />
          <p className="text-xs text-brand-500 mt-1">
            Üresen hagyva azonnal életbe lép a bekapcsoláskor.
          </p>
        </div>
        <div>
          <label className="label">Időzítés — eddig (opcionális)</label>
          <input
            type="datetime-local"
            className="input"
            value={isoToLocalInput(v.endAt)}
            onChange={(e) =>
              onChange({ endAt: localInputToIso(e.target.value) })
            }
          />
          <p className="text-xs text-brand-500 mt-1">
            Ekkor {isSite ? "az oldal" : "a projekt"} MAGÁTÓL élesre vált — nem
            kell visszakapcsolni.
          </p>
        </div>
      </div>

      <div>
        <label className="label">Cím</label>
        <input
          className="input"
          placeholder={DEFAULT_MAINTENANCE_TITLE}
          value={v.title ?? ""}
          onChange={(e) => onChange({ title: e.target.value })}
        />
      </div>

      <div>
        <label className="label">Szöveg a cím alatt</label>
        <textarea
          className="input min-h-[80px]"
          placeholder={
            isSite ? DEFAULT_MAINTENANCE_MESSAGE : DEFAULT_PROJECT_MAINTENANCE_MESSAGE
          }
          value={v.message ?? ""}
          onChange={(e) => onChange({ message: e.target.value })}
        />
      </div>

      {isSite && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={v.showContact !== false}
            onChange={(e) => onChange({ showContact: e.target.checked })}
          />
          Telefonszám és e-mail cím megjelenítése a „Hamarosan" oldalon
        </label>
      )}
    </div>
  );
}

/** ISO időbélyeg → `datetime-local` mező értéke (helyi idő szerint). */
export function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const d = new Date(t);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** `datetime-local` mező értéke (helyi idő) → ISO időbélyeg; üres → null. */
export function localInputToIso(v: string): string | null {
  if (!v) return null;
  const t = Date.parse(v);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}
